import React, { useState } from 'react';
import { X, User, Phone, Mail, Calendar, DollarSign, FileText, Tag, Receipt, CheckCircle, AlertTriangle, ArrowRight, MessageSquare } from 'lucide-react';

interface OrderItem {
  id: string;
  item_type: string;
  service_type: string;
  quantity: number;
  unit_price: number;
  notes?: string;
}

interface ParsedItemData {
  notes?: string;
  color?: string;
  issues?: string[];
  customIssues?: string[];
  stainLocations?: string[];
  selectedServices?: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  selectedAddons?: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  selectedSpecialties?: Array<{
    id: string;
    name: string;
    surcharge: number;
    type: string;
  }>;
  rfidTags?: string[];
  workflow_stage?: string;
  service_level?: string;
}

interface Customer {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

interface Order {
  id: string;
  customer_id: string;
  status: string;
  total_amount: number;
  paid_amount: number;
  notes?: string;
  due_date?: string;
  created_at: string;
  updated_at: string;
  customers: Customer;
  order_items: OrderItem[];
}

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onOrderUpdate?: () => void; // Callback to refresh station data
  stationId?: string; // Current station ID for station-specific functionality
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ isOpen, onClose, order, onOrderUpdate, stationId }) => {
  const [showIssueOptions, setShowIssueOptions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rfidTags, setRfidTags] = useState<Record<string, string>>({}); // Store RFID tags for each item
  const [showRfidSection, setShowRfidSection] = useState(false);
  
  if (!isOpen || !order) return null;

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const formatTime = (dateString: string) => new Date(dateString).toLocaleTimeString();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'stain_removal': return 'bg-purple-100 text-purple-800';
      case 'alteration': return 'bg-orange-100 text-orange-800';
      case 'wash_dry': return 'bg-cyan-100 text-cyan-800';
      case 'fold_press': return 'bg-green-100 text-green-800';
      case 'ready': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleVerifyComplete = async () => {
    setIsProcessing(true);
    try {
      const nextStatus = getNextStatus(order.status);
      await updateOrderStatus(nextStatus);
      onClose();
    } catch (error) {
      console.error('Failed to complete order:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReportIssue = () => {
    setShowIssueOptions(!showIssueOptions);
  };

  const handleIssueType = async (issueType: string) => {
    setIsProcessing(true);
    try {
      await updateOrderStatus('issue_reported', `Issue reported: ${issueType}`);
      onClose();
    } catch (error) {
      console.error('Failed to report issue:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNotifyCustomer = async (issueType: string) => {
    setIsProcessing(true);
    try {
      // Update status to require additional service
      await updateOrderStatus('additional_service_required', `Additional service required: ${issueType}`);
      
      // Here you would integrate with your SMS/notification system
      alert(`Customer ${order.customers.name} will be notified about: ${issueType}`);
      
      onClose();
    } catch (error) {
      console.error('Failed to notify customer:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateOrderStatus = async (status: string, notes?: string) => {
    const response = await fetch(`/api/kds/status/${order.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status,
        notes: notes || `Order ${status.replace('_', ' ')} via TDS`
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update order status');
    }

    // Refresh station data if callback provided
    if (onOrderUpdate) {
      onOrderUpdate();
    }
  };

  const getNextStatus = (currentStatus: string): string => {
    // Get the current workflow stage from order notes to determine next stage
    let workflowStage = '';
    if (order?.notes) {
      try {
        const noteData = JSON.parse(order.notes);
        workflowStage = noteData.workflow_stage || '';
      } catch {
        // If notes aren't JSON, check for simple text match
        const match = order.notes.match(/workflow_stage:(\w+)/);
        workflowStage = match ? match[1] : '';
      }
    }

    // Check if order has stains or issues that require special handling
    const hasStains = order?.order_items?.some((item: any) => {
      if (item.notes) {
        try {
          const itemData = JSON.parse(item.notes);
          return itemData.issues && itemData.issues.length > 0;
        } catch {
          return false;
        }
      }
      return false;
    });

    // Map current workflow stage to next stage
    const statusFlow: Record<string, string> = {
      // Based on current order status/workflow stage
      'pending': hasStains ? 'stain_removal' : 'wash_dry',    // Check for stains first
      'processing': hasStains ? 'stain_removal' : 'wash_dry', // Check for stains first
      'stain_removal': 'wash_dry',        // Stain Removal → Wash & Dry
      'wash_dry': 'fold_press',           // Wash & Dry → Fold & Press
      'fold_press': 'ready',              // Fold & Press → Ready (Pickup)
      'ready': 'completed',               // Pickup → Completed
      'issue_reported': 'processing',     // Issues → Back to Processing
      'additional_service_required': 'processing'
    };

    // If order has workflow stage, use that, otherwise use order status
    const currentStage = workflowStage || currentStatus;
    return statusFlow[currentStage] || 'ready';
  };

  // Generate RFID tag for item
  const generateRFIDTag = () => {
    return Math.random().toString(36).substr(2, 8).toUpperCase();
  };

  // Get full service description
  const getFullServiceName = (item: any) => {
    // Map service IDs to full descriptive names
    const serviceMap: Record<string, string> = {
      'wash_and_fold': 'Wash & Fold + Ironing / Pressing',
      'dry_cleaning': 'Dry Cleaning',
      'wet_cleaning': 'Wet Cleaning',
      'hand_washing': 'Hand Washing',
      'ironing': 'Ironing / Pressing',
      'steam_finishing': 'Steam Finishing',
      'alterations': 'Alterations'
    };
    
    // Check service category first, then service type, then extract from name
    let serviceName = serviceMap[item.service?.category || item.service_type] || 
                     serviceMap[item.service?.id];
    
    if (!serviceName && item.service?.name) {
      // Extract service part from name like "Polo Shirt (Wash & Fold + Ironing / Pressing)"
      const match = item.service.name.match(/\((.*)\)$/);
      if (match) {
        serviceName = match[1];
      } else {
        // If no parentheses, check if it's just the service name
        serviceName = item.service.name.includes('Polo Shirt') ? 'Wash & Fold + Ironing / Pressing' : item.service.name;
      }
    }
    
    return serviceName || item.service_type || 'Standard Service';
  };

  // View receipt handler
  const handleViewReceipt = () => {
    const receiptData = {
      orderNumber: order.id?.toString().slice(-6) || '000000',
      date: new Date().toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      }),
      customer: order.customers,
      order: { items: order.order_items || [] },
      serviceLevel: 'standard', // Default for KDS view
      totalAmount: order.total_amount || 0,
      paymentMethod: 'cash', // Default for KDS view
      pickupDate: order.due_date ? new Date(order.due_date).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric', 
        month: 'long',
        year: 'numeric'
      }) : 'TBD'
    };

    // Generate receipt HTML and open in new window
    const receiptWindow = window.open('', '_blank', 'width=600,height=800,scrollbars=yes');
    if (receiptWindow) {
      receiptWindow.document.write(generateReceiptHTML(receiptData));
      receiptWindow.document.close();
      receiptWindow.focus();
    }
  };

  // View garment ticket handler
  const handleViewGarmentTicket = () => {
    // Generate a garment ticket for each item in the order
    order.order_items?.forEach((item: any, index: number) => {
      const garmentData = {
        orderNumber: order.id?.toString().slice(-6) || '000000',
        customerName: order.customers?.name || 'N/A',
        pickupDate: order.due_date ? new Date(order.due_date).toLocaleDateString('en-GB', {
          day: 'numeric', 
          month: 'short',
          year: 'numeric'
        }) : 'TBD',
        item: item,
        itemIndex: index + 1
      };

      // Generate garment ticket HTML and open in new window
      const ticketWindow = window.open('', '_blank', 'width=400,height=600,scrollbars=yes');
      if (ticketWindow) {
        ticketWindow.document.write(generateGarmentTicketHTML(garmentData));
        ticketWindow.document.close();
        ticketWindow.focus();
      }
    });
  };

  // Generate RFID for specific item
  const generateRFIDForItem = (itemId: string) => {
    const newRfid = Math.random().toString(36).substr(2, 8).toUpperCase();
    setRfidTags(prev => ({ ...prev, [itemId]: newRfid }));
    return newRfid;
  };

  // Generate RFID for all items
  const generateAllRFIDs = () => {
    const newTags: Record<string, string> = {};
    order?.order_items?.forEach(item => {
      if (!rfidTags[item.id]) {
        newTags[item.id] = Math.random().toString(36).substr(2, 8).toUpperCase();
      }
    });
    setRfidTags(prev => ({ ...prev, ...newTags }));
  };

  // Print garment tickets with RFID
  const printGarmentTicketsWithRFID = () => {
    order?.order_items?.forEach((item: any, index: number) => {
      const rfidTag = rfidTags[item.id] || generateRFIDForItem(item.id);
      
      const garmentData = {
        orderNumber: order.id?.toString().slice(-6) || '000000',
        customerName: order.customers?.name || 'N/A',
        pickupDate: order.due_date ? new Date(order.due_date).toLocaleDateString('en-GB', {
          day: 'numeric', 
          month: 'short',
          year: 'numeric'
        }) : 'TBD',
        item: item,
        itemIndex: index + 1,
        rfidTag: rfidTag
      };

      // Generate garment ticket HTML and open in new window
      const ticketWindow = window.open('', '_blank', 'width=400,height=600,scrollbars=yes');
      if (ticketWindow) {
        ticketWindow.document.write(generateGarmentTicketWithRFIDHTML(garmentData));
        ticketWindow.document.close();
        ticketWindow.focus();
      }
    });
  };

  // View RFID tag handler
  const handleViewRFIDTag = () => {
    if (stationId === 'processing') {
      setShowRfidSection(!showRfidSection);
    } else {
      // For other stations, show existing RFID info
      alert('RFID Tag view functionality - showing existing tags!');
    }
  };

  // Generate receipt HTML
  const generateReceiptHTML = (data: any) => {
    const getServiceLevelInfo = (level: string) => {
      switch (level) {
        case 'express': return { name: 'Express (24-48 Hours)', fee: '£4.50' };
        case 'rush': return { name: 'Rush (Same Day)', fee: '£9.00' };
        default: return { name: 'Standard (2-3 Days)', fee: '£0.00' };
      }
    };

    const serviceInfo = getServiceLevelInfo(data.serviceLevel);
    const subtotal = data.order.items.reduce((sum: number, item: any) => sum + (item.unit_price || 0), 0);
    const expressFeePct = data.serviceLevel === 'express' ? 25 : 0;
    const rushFeePct = data.serviceLevel === 'rush' ? 50 : 0;
    const expressFee = subtotal * (expressFeePct / 100);
    const rushFee = subtotal * (rushFeePct / 100);

    return `
<!DOCTYPE html>
<html>
<head>
    <title>SmartDry Receipt - Order #${data.orderNumber}</title>
    <style>
        body {
            font-family: 'Courier New', monospace;
            line-height: 1.4;
            max-width: 400px;
            margin: 20px auto;
            padding: 20px;
            background: white;
        }
        .receipt {
            white-space: pre-line;
            font-size: 12px;
        }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .separator { border-top: 1px dashed #000; margin: 10px 0; }
        @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="receipt">
----------------------------------------
             SMARTDRY CLEANERS
         77 Fulham Palace Road, Office 1.62z
           London, W6 8JA United Kingdom
             www.smartdry.com | +44 xxx

----------------------------------------
🧾 Order #: ${data.orderNumber}       Date: ${data.date}
Customer: ${data.customer?.name || 'N/A'}
Phone: ${data.customer?.phone || 'N/A'}
Email: ${data.customer?.email || 'N/A'}
----------------------------------------

👕 ITEMS:
${data.order.items.map((item: any, index: number) => {
  const itemData = item.notes ? (() => {
    try { return JSON.parse(item.notes); }
    catch { return {}; }
  })() : {};
  
  return `${index + 1}. ${item.item_type || 'Item'}
   - Service: ${getFullServiceName(item)}
   - Color: ${itemData.color || 'Not specified'}
   - Fabric: Cotton
   - RFID: ${generateRFIDTag()}
   - Price: £${(item.unit_price || 0).toFixed(2)}`;
}).join('\n\n')}

----------------------------------------
🕓 SERVICE LEVEL:
- ${serviceInfo.name}
- Pickup: ${data.pickupDate}

----------------------------------------
💳 PAYMENT SUMMARY:
Subtotal:             £${subtotal.toFixed(2)}
Express Fee:          £${expressFee.toFixed(2)}
Rush Fee:             £${rushFee.toFixed(2)}
----------------------------------------
TOTAL:                £${data.totalAmount.toFixed(2)}

✅ PAID: ${data.paymentMethod === 'cash' ? 'Cash' : 'Card (Stripe Terminal)'}

----------------------------------------
📌 NOTES:
- Items are insured up to £200.
- Unclaimed orders after 30 days may incur storage charges.

Thank you for using SmartDry!
----------------------------------------
    </div>
    
    <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            🖨️ Print Receipt
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
            ❌ Close
        </button>
    </div>
</body>
</html>`;
  };

  // Generate garment ticket HTML
  const generateGarmentTicketHTML = (data: any) => {
    const itemData = data.item.notes ? (() => {
      try { return JSON.parse(data.item.notes); }
      catch { return {}; }
    })() : {};

    // Get stain information
    const stains = itemData.issues ? itemData.issues.map((issue: string) => {
      switch(issue) {
        case 'wine': return 'Wine (Collar)';
        case 'oil': return 'Oil Stain';
        case 'ink': return 'Ink Stain';
        case 'blood': return 'Blood Stain';
        case 'sweat': return 'Sweat Stains';
        case 'food': return 'Food Stain';
        case 'makeup': return 'Makeup Stain';
        case 'grass': return 'Grass Stain';
        case 'mud': return 'Mud Stain';
        case 'paint': return 'Paint Stain';
        default: return issue;
      }
    }).join(', ') : 'None';

    return `
<!DOCTYPE html>
<html>
<head>
    <title>SmartDry Garment Ticket - ${data.item.item_type}</title>
    <style>
        body {
            font-family: 'Courier New', monospace;
            line-height: 1.4;
            max-width: 400px;
            margin: 20px auto;
            padding: 20px;
            background: white;
            font-size: 13px;
        }
        .ticket {
            border: 3px solid #000;
            background: white;
        }
        .header {
            background: #f8f9fa;
            border-bottom: 2px solid #000;
            text-align: center;
            padding: 10px;
            font-weight: bold;
            font-size: 16px;
        }
        .section {
            border-bottom: 2px solid #000;
            padding: 10px;
        }
        .section:last-child {
            border-bottom: none;
        }
        .row {
            display: flex;
            margin: 5px 0;
            align-items: center;
        }
        .icon {
            margin-right: 8px;
            width: 20px;
        }
        .label {
            font-weight: bold;
            margin-right: 5px;
        }
        @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="ticket">
        <div class="header">
            SMARTDRY CLEANERS
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">🧾</span>
                <span class="label">Order #:</span>
                <span>${data.orderNumber}</span>
            </div>
            <div class="row">
                <span class="icon">👤</span>
                <span class="label">Customer:</span>
                <span>${data.customerName}</span>
            </div>
            <div class="row">
                <span class="icon">📅</span>
                <span class="label">Pickup:</span>
                <span>${data.pickupDate}</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">👕</span>
                <span class="label">Item:</span>
                <span>${data.item.item_type}</span>
            </div>
            <div class="row">
                <span class="icon">🎨</span>
                <span class="label">Color:</span>
                <span>${itemData.color || 'Not specified'}</span>
            </div>
            <div class="row">
                <span class="icon">🧵</span>
                <span class="label">Service:</span>
                <span>${getFullServiceName(data.item)}</span>
            </div>
            <div class="row">
                <span class="icon">🏷️</span>
                <span class="label">RFID:</span>
                <span>${generateRFIDTag()}</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">⚠️</span>
                <span class="label">Stain:</span>
                <span>${stains}</span>
            </div>
            <div class="row">
                <span class="icon">📎</span>
                <span class="label">Special Instructions:</span>
                <span>None</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">📍</span>
                <span class="label">Station:</span>
                <span>Processing</span>
            </div>
        </div>
    </div>
    
    <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            🖨️ Print Ticket
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
            ❌ Close
        </button>
    </div>
</body>
</html>`;
  };

  // Generate garment ticket with RFID HTML
  const generateGarmentTicketWithRFIDHTML = (data: any) => {
    const itemData = data.item.notes ? (() => {
      try { return JSON.parse(data.item.notes); }
      catch { return {}; }
    })() : {};

    // Get stain information
    const stains = itemData.issues ? itemData.issues.map((issue: string) => {
      switch(issue) {
        case 'wine': return 'Wine (Collar)';
        case 'oil': return 'Oil Stain';
        case 'ink': return 'Ink Stain';
        case 'blood': return 'Blood Stain';
        case 'sweat': return 'Sweat Stains';
        case 'food': return 'Food Stain';
        case 'makeup': return 'Makeup Stain';
        case 'grass': return 'Grass Stain';
        case 'mud': return 'Mud Stain';
        case 'paint': return 'Paint Stain';
        default: return issue;
      }
    }).join(', ') : 'None';

    return `
<!DOCTYPE html>
<html>
<head>
    <title>SmartDry Garment Ticket - ${data.item.item_type}</title>
    <style>
        body {
            font-family: 'Courier New', monospace;
            line-height: 1.4;
            max-width: 400px;
            margin: 20px auto;
            padding: 20px;
            background: white;
            font-size: 13px;
        }
        .ticket {
            border: 3px solid #000;
            background: white;
        }
        .header {
            background: #f8f9fa;
            border-bottom: 2px solid #000;
            text-align: center;
            padding: 10px;
            font-weight: bold;
            font-size: 16px;
        }
        .section {
            border-bottom: 2px solid #000;
            padding: 10px;
        }
        .section:last-child {
            border-bottom: none;
        }
        .row {
            display: flex;
            margin: 5px 0;
            align-items: center;
        }
        .icon {
            margin-right: 8px;
            width: 20px;
        }
        .label {
            font-weight: bold;
            margin-right: 5px;
        }
        .rfid-highlight {
            background: #fffacd;
            padding: 2px 4px;
            border: 1px solid #ddd;
            font-weight: bold;
        }
        @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="ticket">
        <div class="header">
            SMARTDRY CLEANERS
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">🧾</span>
                <span class="label">Order #:</span>
                <span>${data.orderNumber}</span>
            </div>
            <div class="row">
                <span class="icon">👤</span>
                <span class="label">Customer:</span>
                <span>${data.customerName}</span>
            </div>
            <div class="row">
                <span class="icon">📅</span>
                <span class="label">Pickup:</span>
                <span>${data.pickupDate}</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">👕</span>
                <span class="label">Item:</span>
                <span>${data.item.item_type}</span>
            </div>
            <div class="row">
                <span class="icon">🎨</span>
                <span class="label">Color:</span>
                <span>${itemData.color || 'Not specified'}</span>
            </div>
            <div class="row">
                <span class="icon">🧵</span>
                <span class="label">Service:</span>
                <span>${getFullServiceName(data.item)}</span>
            </div>
            <div class="row">
                <span class="icon">🏷️</span>
                <span class="label">RFID:</span>
                <span class="rfid-highlight">${data.rfidTag}</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">⚠️</span>
                <span class="label">Stain:</span>
                <span>${stains}</span>
            </div>
            <div class="row">
                <span class="icon">📎</span>
                <span class="label">Special Instructions:</span>
                <span>None</span>
            </div>
        </div>
        
        <div class="section">
            <div class="row">
                <span class="icon">📍</span>
                <span class="label">Station:</span>
                <span>Processing</span>
            </div>
        </div>
    </div>
    
    <div class="no-print" style="text-align: center; margin-top: 20px;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            🖨️ Print Ticket
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
            ❌ Close
        </button>
    </div>
</body>
</html>`;
  };

  const parseItemData = (notes?: string): ParsedItemData => {
    if (!notes) return {};
    
    try {
      // Try to parse as JSON (structured data from POS)
      const parsed = JSON.parse(notes);
      return parsed;
    } catch {
      // If not JSON, treat as regular notes
      return { notes };
    }
  };

  const getColorValue = (colorName: string): string => {
    const colorMap: Record<string, string> = {
      'white': '#FFFFFF',
      'black': '#000000',
      'red': '#DC2626',
      'blue': '#2563EB',
      'green': '#16A34A',
      'yellow': '#CA8A04',
      'purple': '#9333EA',
      'pink': '#EC4899',
      'gray': '#6B7280',
      'grey': '#6B7280',
      'brown': '#92400E',
      'navy': '#1E40AF',
      'beige': '#F5F5DC',
      'cream': '#FFFDD0'
    };
    return colorMap[colorName.toLowerCase()] || '#9CA3AF';
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <Receipt className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
              <p className="text-sm text-gray-500">Order ID: {order.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Order Status */}
          <div className="flex items-center justify-between">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
              {order.status.replace('_', ' ').toUpperCase()}
            </span>
            <div className="text-right">
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">{formatDate(order.created_at)} at {formatTime(order.created_at)}</p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{order.customers.name}</p>
              </div>
              {order.customers.phone && (
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{order.customers.phone}</p>
                  </div>
                </div>
              )}
              {order.customers.email && (
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{order.customers.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected Services */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Tag className="w-5 h-5 mr-2" />
              Selected Services
            </h3>
            <div className="space-y-3">
              {order.order_items.map((item, index) => {
                const itemData = parseItemData(item.notes);
                
                return (
                <div key={index} className="bg-white p-3 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-900">{item.item_type}</p>
                      <p className="text-xs text-blue-600 font-medium">Qty: {item.quantity}</p>
                </div>
                    
                    {/* SmartDry Core Services */}
                    {itemData?.selectedServices && itemData.selectedServices.length > 0 ? (
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Services:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {itemData.selectedServices.map((service, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                🧵 {service.name}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {itemData.selectedAddons && itemData.selectedAddons.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-600 font-medium">Selected Add-ons:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.selectedAddons.map((addon, idx) => (
                                <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                                  ➕ {addon.name} (+${addon.price.toFixed(2)})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {itemData.selectedSpecialties && itemData.selectedSpecialties.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-600 font-medium">Specialties:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.selectedSpecialties.map((specialty, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  🔸 {specialty.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Fallback to basic service type
                      <p className="text-sm text-gray-600">{item.service_type.replace('_', ' ').toUpperCase()}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Items with RFID Tags */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Tag className="w-5 h-5 mr-2" />
              Items & RFID Tags
            </h3>
            <div className="space-y-3">
              {order.order_items.map((item, index) => {
                const itemData = parseItemData(item.notes);
                
                return (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-4">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                          Item {index + 1}
                        </span>
                        <h4 className="font-semibold text-gray-900">{item.item_type}</h4>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(item.unit_price * item.quantity)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Service Type</p>
                        <p className="font-medium">{getFullServiceName(item)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Quantity</p>
                        <p className="font-medium">{item.quantity}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Unit Price</p>
                        <p className="font-medium">{formatCurrency(item.unit_price)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Item Color</p>
                        <p className="font-medium">
                          {itemData.color || 'Not specified'}
                          {itemData.color && (
                            <span 
                              className="inline-block w-4 h-4 rounded-full ml-2 border border-gray-300"
                              style={{ backgroundColor: getColorValue(itemData.color) }}
                            ></span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    {/* Issues and Stains */}
                    {(itemData.issues?.length || itemData.customIssues?.length || itemData.stainLocations?.length) && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h5 className="font-medium text-red-900 mb-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Issues & Stains Detected
                        </h5>
                        
                        {itemData.issues && itemData.issues.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-red-700 font-medium">Issue Tags:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.issues.map((issue, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  {issue === 'wine' ? '🍷' : '⚠️'} {issue}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {itemData.stainLocations && itemData.stainLocations.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-red-700 font-medium">Stain Locations:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.stainLocations.map((location, idx) => (
                                <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                  📍 {location}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {itemData.customIssues && itemData.customIssues.length > 0 && (
                          <div>
                            <p className="text-xs text-red-700 font-medium">Custom Issues:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.customIssues.map((issue, idx) => (
                                <span key={idx} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                  🔧 {issue}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* SmartDry Core Services Information */}
                    {(itemData.selectedServices?.length || itemData.selectedAddons?.length || itemData.selectedSpecialties?.length) && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          SmartDry Core Services
                        </h5>
                        
                        {itemData.selectedServices && itemData.selectedServices.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-blue-700 font-medium">Services:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.selectedServices.map((service, idx) => (
                                <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  🧵 {service.name} (${service.price.toFixed(2)})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {itemData.selectedAddons && itemData.selectedAddons.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-blue-700 font-medium">Selected Add-ons:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.selectedAddons.map((addon, idx) => (
                                <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                                  ➕ {addon.name} (+${addon.price.toFixed(2)})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {itemData.selectedSpecialties && itemData.selectedSpecialties.length > 0 && (
                          <div>
                            <p className="text-xs text-blue-700 font-medium">Special Handling:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {itemData.selectedSpecialties.map((specialty, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  🔸 {specialty.name} ({specialty.type === 'fixed' ? `+$${specialty.surcharge.toFixed(2)}` : `+${specialty.surcharge}%`})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* RFID Tags from Order Data */}
                    {itemData.rfidTags && itemData.rfidTags.length > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          RFID Tags
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {itemData.rfidTags.map((tag, idx) => (
                            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-mono rounded-full border">
                              🏷️ {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {itemData.notes && (
                      <div className="mt-2">
                        <p className="text-gray-500 text-sm">Notes</p>
                        <p className="text-sm bg-yellow-50 p-2 rounded">{itemData.notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment & Receipt Information */}
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Payment & Receipt
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(order.total_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid Amount</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(order.paid_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Status</p>
                <p className="font-medium">
                  {order.paid_amount >= order.total_amount ? (
                    <span className="text-green-600">✅ Paid in Full</span>
                  ) : (
                    <span className="text-orange-600">⚠️ Partial Payment</span>
                  )}
                </p>
              </div>
            </div>
            
            {order.paid_amount > order.total_amount && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Change Given:</strong> {formatCurrency(order.paid_amount - order.total_amount)}
                </p>
              </div>
            )}
          </div>

          {/* Due Date */}
          {order.due_date && (
            <div className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-600">Due Date</p>
                <p className="font-medium text-yellow-800">{formatDate(order.due_date)}</p>
              </div>
            </div>
          )}

          {/* Order Notes */}
          {order.notes && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Order Notes
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">{order.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Verify & Complete */}
            <button 
              onClick={handleVerifyComplete}
              disabled={isProcessing}
              className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-5 h-5" />
              <span>{isProcessing ? 'Processing...' : 'Verify & Complete'}</span>
            </button>
            
            {/* Report Issue */}
            <button 
              onClick={handleReportIssue}
              className="flex items-center justify-center space-x-2 bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              <AlertTriangle className="w-5 h-5" />
              <span>Report Issue</span>
            </button>
            
            {/* Send to Next Stage */}
            <button 
              onClick={handleVerifyComplete}
              disabled={isProcessing}
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <ArrowRight className="w-5 h-5" />
              <span>
                {order.status === 'ready' ? 'Mark as Completed' : 'Send to Next'}
              </span>
            </button>
          </div>

          {/* View & Print Documents */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-3">View Documents</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button 
                onClick={handleViewReceipt}
                className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>View Receipt</span>
              </button>
              
              <button 
                onClick={handleViewGarmentTicket}
                className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a1.994 1.994 0 01-1.414.586H7a4 4 0 01-4-4v-6a4 4 0 014-4z" />
                </svg>
                <span>View Garment Ticket</span>
              </button>
              
              <button 
                onClick={handleViewRFIDTag}
                className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>View RFID Tag</span>
              </button>
            </div>
          </div>

          {/* RFID Management (Processing Station Only) */}
          {stationId === 'processing' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-3">🏷️ RFID Tag Management</h4>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <button 
                    onClick={generateAllRFIDs}
                    className="flex items-center justify-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a1.994 1.994 0 01-1.414.586H7a4 4 0 01-4-4v-6a4 4 0 014-4z" />
                    </svg>
                    <span>Generate All RFID Tags</span>
                  </button>
                  
                  <button 
                    onClick={printGarmentTicketsWithRFID}
                    className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H9.5a2 2 0 01-2-2V5a2 2 0 012-2H14" />
                    </svg>
                    <span>Print Garment Tickets</span>
                  </button>
                </div>

                {/* Individual Item RFID Management */}
                <div className="grid gap-2">
                  {order?.order_items?.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-yellow-200 rounded">
                      <div className="flex-1">
                        <span className="font-medium">{item.item_type}</span>
                        <span className="text-sm text-gray-500 ml-2">({getFullServiceName(item)})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {rfidTags[item.id] ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-mono">
                            {rfidTags[item.id]}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-sm">
                            No RFID
                          </span>
                        )}
                        <button
                          onClick={() => generateRFIDForItem(item.id)}
                          className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 transition-colors"
                        >
                          {rfidTags[item.id] ? 'Regenerate' : 'Generate'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Issue Types (Shown when Report Issue is clicked) */}
          {showIssueOptions && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="font-medium text-orange-900 mb-2">Select Issue Type:</h4>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => handleIssueType('Wine Stain')}
                  className="text-left p-2 bg-white border border-orange-200 rounded hover:bg-orange-50"
                >
                  🍷 Stain Removal (Wine, Food)
                </button>
                <button 
                  onClick={() => handleIssueType('Repair Required')}
                  className="text-left p-2 bg-white border border-orange-200 rounded hover:bg-orange-50"
                >
                  🔧 Repair (Buttons, Zippers)
                </button>
                <button 
                  onClick={() => handleIssueType('Alteration Needed')}
                  className="text-left p-2 bg-white border border-orange-200 rounded hover:bg-orange-50"
                >
                  ✂️ Alteration Required
                </button>
                <button 
                  onClick={() => handleIssueType('Quality Issue')}
                  className="text-left p-2 bg-white border border-orange-200 rounded hover:bg-orange-50"
                >
                  🔍 Quality Issue
                </button>
              </div>
              <div className="mt-3 flex space-x-2">
                <button 
                  onClick={() => handleNotifyCustomer('Additional Service Required')}
                  className="flex items-center space-x-1 bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Notify Customer</span>
                </button>
                <button 
                  onClick={() => setShowIssueOptions(false)}
                  className="bg-gray-300 text-gray-700 px-3 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
